// Self-contained image crop: drag a rectangle over the area to keep, Apply crops it
// via <canvas> → a new PNG blob. BlockSuite 0.19.5 has no native crop, so this is ours.
import React, { useRef, useState } from 'react';

type Rect = { x: number; y: number; w: number; h: number };

export function CropModal({ src, onApply, onClose }: {
  src: string;
  onApply: (blob: Blob) => void | Promise<void>;
  onClose: () => void;
}): React.ReactElement {
  const imgRef = useRef<HTMLImageElement>(null);
  const areaRef = useRef<HTMLDivElement>(null);
  const startRef = useRef<{ x: number; y: number } | null>(null);
  const [rect, setRect] = useState<Rect | null>(null);
  const [busy, setBusy] = useState(false);

  const rel = (e: React.MouseEvent) => {
    const r = areaRef.current!.getBoundingClientRect();
    return {
      x: Math.max(0, Math.min(e.clientX - r.left, r.width)),
      y: Math.max(0, Math.min(e.clientY - r.top, r.height)),
    };
  };
  const onDown = (e: React.MouseEvent) => { e.preventDefault(); const p = rel(e); startRef.current = p; setRect({ x: p.x, y: p.y, w: 0, h: 0 }); };
  const onMove = (e: React.MouseEvent) => {
    const s = startRef.current;
    if (!s) return;
    const p = rel(e);
    setRect({ x: Math.min(s.x, p.x), y: Math.min(s.y, p.y), w: Math.abs(p.x - s.x), h: Math.abs(p.y - s.y) });
  };
  const onUp = () => { startRef.current = null; };

  const apply = async () => {
    const img = imgRef.current;
    if (!img || !rect || rect.w < 4 || rect.h < 4) return;
    setBusy(true);
    try {
      const sx = img.naturalWidth / img.clientWidth;
      const sy = img.naturalHeight / img.clientHeight;
      const canvas = document.createElement('canvas');
      canvas.width = Math.max(1, Math.round(rect.w * sx));
      canvas.height = Math.max(1, Math.round(rect.h * sy));
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('no 2d context');
      ctx.drawImage(img, rect.x * sx, rect.y * sy, rect.w * sx, rect.h * sy, 0, 0, canvas.width, canvas.height);
      const blob: Blob | null = await new Promise((res) => canvas.toBlob((b) => res(b), 'image/png'));
      if (blob) await onApply(blob);
      onClose();
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[docs] crop failed', e);
      window.alert('Could not crop the image');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fx-modal-backdrop" onClick={onClose}>
      <div className="fx-modal fx-crop" onClick={(e) => e.stopPropagation()}>
        <div className="fx-login-title">Crop image</div>
        <div className="fx-crop-hint">Drag a rectangle over the area you want to keep.</div>
        <div
          className="fx-crop-area"
          ref={areaRef}
          onMouseDown={onDown}
          onMouseMove={onMove}
          onMouseUp={onUp}
          onMouseLeave={onUp}
        >
          <img ref={imgRef} src={src} className="fx-crop-img" draggable={false} alt="" />
          {rect && rect.w > 1 && rect.h > 1 && (
            <div className="fx-crop-rect" style={{ left: rect.x, top: rect.y, width: rect.w, height: rect.h }} />
          )}
        </div>
        <div className="fx-modal-actions">
          <button type="button" className="fx-sendbtn" onClick={apply} disabled={busy || !rect || rect.w < 4 || rect.h < 4}>
            {busy ? 'Cropping…' : 'Apply crop'}
          </button>
          <button type="button" className="fx-login-back" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
}
